import { cn } from '@commander/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { AlertCircle, CheckCircle, Info, TrendingUp, User, Users } from '../../icons';

const badgeVariants = cva(
  'inline-flex items-center h-6 px-2 rounded text-xs font-medium transition-all duration-300 flex-shrink-0',
  {
    variants: {
      variant: {
        // Notification badges with light solid backgrounds and borders
        nextStep:
          '[background:hsl(var(--badge-purple-bg))] text-purple-700 border [border-color:hsl(280_85%_85%)] dark:[background:hsl(280_85%_25%)] dark:text-purple-300 dark:[border-color:hsl(280_85%_35%)]',
        contacts:
          '[background:hsl(var(--badge-blue-bg))] text-blue-700 border [border-color:hsl(199_89%_85%)] dark:[background:hsl(199_89%_20%)] dark:text-blue-300 dark:[border-color:hsl(199_89%_30%)]',
        status:
          '[background:hsl(var(--badge-green-bg))] text-green-700 border [border-color:hsl(158_58%_85%)] dark:[background:hsl(142_71%_20%)] dark:text-green-300 dark:[border-color:hsl(142_71%_30%)]',
        intel:
          '[background:hsl(var(--badge-orange-bg))] text-orange-700 border [border-color:hsl(25_95%_85%)] dark:[background:hsl(25_95%_20%)] dark:text-orange-300 dark:[border-color:hsl(25_95%_30%)]',
        prospects:
          '[background:hsl(var(--badge-teal-bg))] text-teal-700 border [border-color:hsl(176_81%_85%)] dark:[background:hsl(176_81%_15%)] dark:text-teal-300 dark:[border-color:hsl(176_81%_25%)]',

        // Status badges with light solid backgrounds (no borders)
        engaged: '[background:hsl(var(--badge-active-bg))] [color:hsl(var(--badge-active-fg))]',
        targeting: '[background:hsl(var(--badge-warm-bg))] [color:hsl(var(--badge-warm-fg))]',
        customer: '[background:hsl(var(--blue-200))] [color:hsl(var(--blue-700))]',
        pending: '[background:hsl(var(--gray-200))] [color:hsl(var(--gray-900))]',

        // Company status variants
        hot: '[background:hsl(var(--badge-hot-bg))] [color:hsl(var(--badge-hot-fg))]',
        warm: '[background:hsl(var(--badge-warm-bg))] [color:hsl(var(--badge-warm-fg))]',
        active: '[background:hsl(var(--badge-active-bg))] [color:hsl(var(--badge-active-fg))]',
        prospect:
          '[background:hsl(var(--badge-prospect-bg))] [color:hsl(var(--badge-prospect-fg))]',
        notActive:
          '[background:hsl(var(--badge-not-active-bg))] [color:hsl(var(--badge-not-active-fg))]',

        // Company type variants
        standalone:
          '[background:hsl(var(--badge-company-type-bg))] [color:hsl(var(--badge-company-type-fg))]',
        corporate:
          '[background:hsl(var(--badge-company-type-bg))] [color:hsl(var(--badge-company-type-fg))]',

        // Service type variants
        rollOff: '[background:hsl(var(--badge-roll-off-bg))] [color:hsl(var(--badge-roll-off-fg))]',
        frontLoad:
          '[background:hsl(var(--badge-front-load-bg))] [color:hsl(var(--badge-front-load-fg))]',
        compactor:
          '[background:hsl(var(--badge-compactor-bg))] [color:hsl(var(--badge-compactor-fg))]',
        recycling:
          '[background:hsl(var(--badge-recycling-bg))] [color:hsl(var(--badge-recycling-fg))]',
        organics:
          '[background:hsl(var(--badge-organics-bg))] [color:hsl(var(--badge-organics-fg))]',
        hazardous:
          '[background:hsl(var(--badge-hazardous-bg))] [color:hsl(var(--badge-hazardous-fg))]',

        // NBA Priority variants
        priorityHigh:
          '[background:hsl(var(--badge-priority-high-bg))] [color:hsl(var(--badge-priority-high-fg))]',
        priorityMedium:
          '[background:hsl(var(--badge-priority-medium-bg))] [color:hsl(var(--badge-priority-medium-fg))]',
        priorityLow:
          '[background:hsl(var(--badge-priority-low-bg))] [color:hsl(var(--badge-priority-low-fg))]',
        priorityPending: '[background:hsl(var(--gray-200))] [color:hsl(var(--gray-700))]',

        // NBA Action type variants
        actionCall:
          '[background:hsl(var(--badge-action-call-bg))] [color:hsl(var(--badge-action-call-fg))]',
        actionEmail:
          '[background:hsl(var(--badge-action-email-bg))] [color:hsl(var(--badge-action-email-fg))]',
        actionMeeting:
          '[background:hsl(var(--badge-action-meeting-bg))] [color:hsl(var(--badge-action-meeting-fg))]',
        actionFollowUp:
          '[background:hsl(var(--badge-action-follow-up-bg))] [color:hsl(var(--badge-action-follow-up-fg))]',
        actionProposal:
          '[background:hsl(var(--badge-action-proposal-bg))] [color:hsl(var(--badge-action-proposal-fg))]',

        // NBA Status variants
        statusPending:
          '[background:hsl(var(--badge-status-pending-bg))] [color:hsl(var(--badge-status-pending-fg))]',
        statusInProgress:
          '[background:hsl(var(--badge-status-in-progress-bg))] [color:hsl(var(--badge-status-in-progress-fg))]',
        statusCompleted:
          '[background:hsl(var(--badge-status-completed-bg))] [color:hsl(var(--badge-status-completed-fg))]',
        statusDismissed:
          '[background:hsl(var(--badge-status-dismissed-bg))] [color:hsl(var(--badge-status-dismissed-fg))]',
        statusSnoozed:
          '[background:hsl(var(--badge-status-snoozed-bg))] [color:hsl(var(--badge-status-snoozed-fg))]',

        // Prospecting Sales Step variants
        prospectingPending:
          '[background:hsl(var(--badge-prospecting-pending-bg))] [color:hsl(var(--badge-prospecting-pending-fg))]',
        prospectingActive:
          '[background:hsl(var(--badge-prospecting-active-bg))] [color:hsl(var(--badge-prospecting-active-fg))]',
        prospectingCompleted:
          '[background:hsl(var(--badge-prospecting-completed-bg))] [color:hsl(var(--badge-prospecting-completed-fg))]',
        prospectingDismissed:
          '[background:hsl(var(--badge-prospecting-dismissed-bg))] [color:hsl(var(--badge-prospecting-dismissed-fg))]',

        // Quote status variants
        quoteDraft: '[background:hsl(var(--badge-warm-bg))] [color:hsl(var(--badge-warm-fg))]',
        quoteSent: '[background:hsl(var(--blue-200))] [color:hsl(var(--blue-700))]',
        quoteViewed:
          '[background:hsl(var(--badge-prospect-bg))] [color:hsl(var(--badge-prospect-fg))]',
        quoteNegotiation:
          '[background:hsl(var(--badge-quote-negotiation-bg))] [color:hsl(var(--badge-quote-negotiation-fg))]',
        quoteAccepted:
          '[background:hsl(var(--badge-active-bg))] [color:hsl(var(--badge-active-fg))]',
        quoteRejected: '[background:hsl(var(--badge-hot-bg))] [color:hsl(var(--badge-hot-fg))]',
        quoteExpired: '[background:hsl(var(--gray-200))] [color:hsl(var(--gray-900))]',
        quoteProcessed:
          '[background:hsl(var(--badge-quote-processed-bg))] [color:hsl(var(--badge-quote-processed-fg))]',

        // Quotes List Redesign tone variants (U5)
        toneSlate:
          'bg-tone-slate-bg text-tone-slate-fg ring-1 ring-inset ring-tone-slate-ring [--badge-dot:hsl(var(--tone-slate-dot))]',
        toneBlue:
          'bg-tone-blue-bg text-tone-blue-fg ring-1 ring-inset ring-tone-blue-ring [--badge-dot:hsl(var(--tone-blue-dot))]',
        toneViolet:
          'bg-tone-violet-bg text-tone-violet-fg ring-1 ring-inset ring-tone-violet-ring [--badge-dot:hsl(var(--tone-violet-dot))]',
        toneAmber:
          'bg-tone-amber-bg text-tone-amber-fg ring-1 ring-inset ring-tone-amber-ring [--badge-dot:hsl(var(--tone-amber-dot))]',
        toneGreen:
          'bg-tone-green-bg text-tone-green-fg ring-1 ring-inset ring-tone-green-ring [--badge-dot:hsl(var(--tone-green-dot))]',
        toneRed:
          'bg-tone-red-bg text-tone-red-fg ring-1 ring-inset ring-tone-red-ring [--badge-dot:hsl(var(--tone-red-dot))]',
        toneRose:
          'bg-tone-rose-bg text-tone-rose-fg ring-1 ring-inset ring-tone-rose-ring [--badge-dot:hsl(var(--tone-rose-dot))]',
        toneTeal:
          'bg-tone-teal-bg text-tone-teal-fg ring-1 ring-inset ring-tone-teal-ring [--badge-dot:hsl(var(--tone-teal-dot))]',
        toneNeutralOff:
          'bg-white dark:bg-background text-foreground-tertiary ring-1 ring-inset ring-input-border-soft [--badge-dot:hsl(var(--dot-neutral))]',

        // Availability: something exists to reveal
        available: 'border-transparent bg-success/15 text-success-emphasis',
        // Availability: nothing exists to reveal
        unavailable: 'bg-muted text-muted-foreground',
        // Ownership: the tenant already has this record (informational, not success)
        owned: 'border-transparent bg-info/15 text-info-emphasis',

        // Default shadcn variants
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        destructive: 'border-transparent bg-destructive text-destructive-foreground',
        outline: 'text-foreground',
        // A quiet neutral tag that labels rather than signals.
        muted: 'bg-muted text-muted-foreground',
      },
      size: {
        chip: 'h-[22px] px-2 rounded-[6px] gap-1.5 text-xs font-medium',
        filter: 'h-[26px] px-2.5 rounded-[6px] gap-1.5 text-xs font-medium',
        small: 'h-5 px-1.5 text-[10px] gap-1',
        default: 'h-6 px-2 text-xs gap-1.5',
        large: 'h-7 px-2.5 text-sm gap-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  icon?: boolean;
  iconType?: 'user' | 'users' | 'check' | 'alert' | 'trending' | 'info';
  dot?: boolean;
}

const iconMap = {
  user: User,
  users: Users,
  check: CheckCircle,
  alert: AlertCircle,
  trending: TrendingUp,
  info: Info,
};

function Badge({
  className,
  variant,
  size,
  icon = false,
  iconType = 'info',
  dot = false,
  children,
  ...props
}: BadgeProps) {
  const IconComponent = icon ? iconMap[iconType] : null;

  const iconSize = size === 'small' ? 'h-3 w-3' : size === 'large' ? 'h-4 w-4' : 'h-3.5 w-3.5';

  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {dot && (
        <span
          data-slot="badge-dot"
          className="size-1.5 rounded-full shrink-0 [background:var(--badge-dot,currentColor)]"
        />
      )}
      {IconComponent && <IconComponent className={iconSize} />}
      {children}
    </div>
  );
}

// Notification Badge Component
export interface NotificationBadgeProps extends BadgeProps {
  type: 'nextStep' | 'contacts' | 'status' | 'intel' | 'prospects';
  count?: number;
}

export function NotificationBadge({ type, count, className, ...props }: NotificationBadgeProps) {
  const iconTypes = {
    nextStep: 'trending',
    contacts: 'users',
    status: 'check',
    intel: 'info',
    prospects: 'user',
  } as const;

  return (
    <Badge variant={type} icon iconType={iconTypes[type]} className={className} {...props}>
      {count !== undefined && count}
      {type.charAt(0).toUpperCase() + type.slice(1).replace(/([A-Z])/g, ' $1')}
    </Badge>
  );
}

// Status Badge Component
export interface StatusBadgeProps extends BadgeProps {
  status: 'engaged' | 'targeting' | 'customer' | 'pending';
}

export function StatusBadge({ status, className, ...props }: StatusBadgeProps) {
  return (
    <Badge variant={status} className={className} {...props}>
      {status}
    </Badge>
  );
}

export { Badge, badgeVariants };
